import React from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from "@mui/material";

import type { BillingInterval, SubscriptionQuoteDto } from '../../models/License';

type SubscriptionQuoteDialogProps = {
    open: boolean;
    quote: SubscriptionQuoteDto | null;
    loading?: boolean;
    onClose: () => void;
    onConfirm: () => void;
};

const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value ?? 0);

const formatDate = (value?: string | null) => {
    if (!value) return "N/A";
    return new Date(value).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const per = (interval?: BillingInterval | null) => (interval === "month" ? "month" : "year");
const describe = (interval?: BillingInterval | null) => (interval === "month" ? "Monthly" : "Annual");

const DetailRow: React.FC<{
    label: string;
    value: React.ReactNode;
    emphasize?: boolean;
}> = ({ label, value, emphasize = false }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography
            variant="body2"
            fontWeight={emphasize ? 700 : 600}
            textAlign="right"
        >
            {value}
        </Typography>
    </Box>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {children}
    </Typography>
);

const SubscriptionQuoteDialog: React.FC<SubscriptionQuoteDialogProps> = ({
    open,
    quote,
    loading = false,
    onClose,
    onConfirm,
}) => {
    if (!quote) return null;

    const timing = quote.changeTiming ?? null; // null = quote for a manual (non-Stripe) plan
    const isUpgrade = quote.actionType?.toUpperCase() === "UPGRADE";
    const isRenew = quote.actionType?.toUpperCase() === "RENEW";
    const nothingToConfirm = timing === "AUTO_RENEW";

    const title =
        timing === "CHECKOUT" ? "Confirm Subscription"
            : timing === "AT_RENEWAL" ? "Schedule Plan Change"
                : timing === "RESUME" ? "Resume Subscription"
                    : timing === "AUTO_RENEW" ? "Your Subscription"
                        : isUpgrade ? "Confirm Upgrade"
                            : isRenew ? "Confirm Renewal"
                                : "Confirm Subscription Change";

    const confirmLabel = loading
        ? "Please wait..."
        : timing === "CHECKOUT" ? "Continue to secure checkout"
            : timing === "AT_RENEWAL" ? "Schedule change"
                : timing === "RESUME" ? "Keep my subscription"
                    : isUpgrade ? "Confirm Upgrade"
                        : "Confirm Renewal";

    // Stripe quotes carry per-interval prices; manual quotes only have annual prices.
    const currentPriceText = quote.currentBillingInterval
        ? `${formatCurrency(quote.currentPrice ?? 0)} / ${per(quote.currentBillingInterval)}`
        : formatCurrency(quote.currentAnnualPrice);
    const targetPriceText = quote.targetBillingInterval
        ? `${formatCurrency(quote.targetPrice ?? 0)} / ${per(quote.targetBillingInterval)}`
        : formatCurrency(quote.targetAnnualPrice);

    const hasCurrentPlan = quote.currentLicensingPlanId > 0;

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{title}</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Alert severity={timing === "AT_RENEWAL" || timing === "RESUME" ? "info" : isUpgrade ? "info" : "success"}>
                        {quote.message}
                    </Alert>

                    {hasCurrentPlan && (
                        <Box>
                            <SectionTitle>Current subscription</SectionTitle>
                            <Stack spacing={1}>
                                <DetailRow label="Plan" value={quote.currentPlanName} />
                                {quote.currentBillingInterval && (
                                    <DetailRow label="Billing" value={describe(quote.currentBillingInterval)} />
                                )}
                                <DetailRow label="Price" value={currentPriceText} />
                                <DetailRow label="Current period ends" value={formatDate(quote.currentLicenseEffectiveToUtc)} />
                            </Stack>
                        </Box>
                    )}

                    {hasCurrentPlan && <Divider />}

                    <Box>
                        <SectionTitle>
                            {timing === "AT_RENEWAL" ? "From your next renewal"
                                : isRenew ? "Next period"
                                    : "New subscription"}
                        </SectionTitle>
                        <Stack spacing={1}>
                            <DetailRow label="Plan" value={quote.targetPlanName} />
                            {quote.targetBillingInterval && (
                                <DetailRow label="Billing" value={describe(quote.targetBillingInterval)} />
                            )}
                            <DetailRow label="Price" value={targetPriceText} />
                            <DetailRow label="Starts" value={formatDate(quote.newLicenseEffectiveFromUtc)} />
                            <DetailRow label="Renews" value={formatDate(quote.newLicenseEffectiveToUtc)} />
                        </Stack>
                    </Box>

                    <Divider />

                    <Box>
                        <SectionTitle>Pricing summary</SectionTitle>
                        <Stack spacing={1}>
                            {timing === "AT_RENEWAL" && (
                                <>
                                    <DetailRow label="Due today" value={formatCurrency(0)} emphasize />
                                    <DetailRow
                                        label={`From ${formatDate(quote.effectiveAtUtc)}`}
                                        value={`${formatCurrency(quote.grossAmount)} / ${per(quote.targetBillingInterval)}`}
                                    />
                                </>
                            )}

                            {(timing === "AUTO_RENEW" || timing === "RESUME") && (
                                <DetailRow
                                    label={`Next payment (${formatDate(quote.effectiveAtUtc)})`}
                                    value={formatCurrency(quote.netAmount)}
                                    emphasize
                                />
                            )}

                            {(timing === "IMMEDIATE" || timing === "CHECKOUT" || timing === null) && (
                                <>
                                    <DetailRow label="Gross amount" value={formatCurrency(quote.grossAmount)} />

                                    {timing === null && isUpgrade && (
                                        <DetailRow
                                            label="Remaining whole months credited"
                                            value={quote.remainingWholeMonthsFromEndOfCurrentMonth}
                                        />
                                    )}

                                    {quote.prorataCredit > 0 && (
                                        <DetailRow
                                            label={timing === "IMMEDIATE" ? "Credit for unused time" : "Pro-rata credit"}
                                            value={`- ${formatCurrency(quote.prorataCredit)}`}
                                        />
                                    )}

                                    <Divider flexItem sx={{ my: 0.5 }} />

                                    <DetailRow
                                        label={timing === "CHECKOUT" ? "Due at checkout" : "Amount due now"}
                                        value={formatCurrency(quote.netAmount)}
                                        emphasize
                                    />
                                </>
                            )}
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            {timing === "AT_RENEWAL"
                                ? "Nothing changes until your renewal date. You can cancel this scheduled change before then."
                                : timing === "IMMEDIATE"
                                    ? "Charged to your saved card. If the payment doesn't go through, your plan stays as it is."
                                    : timing === "CHECKOUT"
                                        ? "Card details are entered on Stripe's secure checkout page."
                                        : timing === null && isUpgrade
                                            ? "Pro-rata credit is currently calculated using whole remaining months after the end of the current month."
                                            : timing === null
                                                ? "Renewal creates a new subscription term and preserves your subscription history."
                                                : ""}
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {nothingToConfirm ? "Close" : "Cancel"}
                </Button>
                {!nothingToConfirm && (
                    <Button
                        variant="contained"
                        onClick={onConfirm}
                        disabled={loading}
                    >
                        {confirmLabel}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SubscriptionQuoteDialog;
